import React, { useState, useRef, useEffect } from 'react';
import Cookies from 'universal-cookie';
import { ChevronDown } from 'lucide-react';
import { CustomDeviceDropdown } from './CustomDeviceDropdown';
import testSoundUrl from '@/audios/test-sound-video.mp3';

const cookies = new Cookies();

function DeviceSetup({ onSetupComplete, isTestMode = false }) {
  const [videoDeviceId, setVideoDeviceId] = useState('');
  const [audioInputDeviceId, setAudioInputDeviceId] = useState('');
  const [audioOutputDeviceId, setAudioOutputDeviceId] = useState('');
  const [permissionsError, setPermissionsError] = useState('');
  const [permissionsSuccess, setPermissionsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [canJoin, setCanJoin] = useState(false);
  const [devices, setDevices] = useState({
    videoDevices: [],
    audioInputDevices: [],
    audioOutputDevices: []
  });
  const [videoStream, setVideoStream] = useState(null);
  const [activeStream, setActiveStream] = useState(null);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [hasUserSelection, setHasUserSelection] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasLoadedFromCookies, setHasLoadedFromCookies] = useState(false);

  const videoRef = useRef(null);
  const audioTestRef = useRef(null);

  // --- Permission Check and Device Initialization ---
  useEffect(() => {
    if (isInitialized) return;

    const checkPermissionsAndInitialize = async () => {
      setIsLoading(true);
      setPermissionsError('');
      try {
        // First, try to enumerate devices to check current state
        let deviceList = await navigator.mediaDevices.enumerateDevices();

        // Check if we have device labels (indicates permissions granted)
        const hasPermissions = deviceList.some(device => device.label !== '');

        if (!hasPermissions) {
          // Request permissions
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          // Stop the tracks immediately, we only needed them for permission prompt
          stream.getTracks().forEach(track => track.stop());

          // Re-enumerate devices now that we have permissions
          deviceList = await navigator.mediaDevices.enumerateDevices();
        }

        // Set available devices
        const videoDevices = deviceList.filter(device => device.kind === 'videoinput');
        const audioInputDevices = deviceList.filter(device => device.kind === 'audioinput');
        const audioOutputDevices = deviceList.filter(device => device.kind === 'audiooutput');

        setDevices({
          videoDevices,
          audioInputDevices,
          audioOutputDevices
        });

        console.log('Available devices:', {
          videoDevices: videoDevices.map(d => ({ id: d.deviceId, label: d.label })),
          audioInputDevices: audioInputDevices.map(d => ({ id: d.deviceId, label: d.label })),
          audioOutputDevices: audioOutputDevices.map(d => ({ id: d.deviceId, label: d.label }))
        });

        // Get stored devices from cookies using fresh instance
        const cookiesInstance = new Cookies();
        const storedVideo = cookiesInstance.get('videoDeviceId');
        const storedAudioIn = cookiesInstance.get('audioInputDeviceId');
        const storedAudioOut = cookiesInstance.get('audioOutputDeviceId');

        console.log('Loaded cookies:', {
          storedVideo,
          storedAudioIn,
          storedAudioOut
        });

        // Get default devices if needed
        const defaults = {
          video: videoDevices[0]?.deviceId || '',
          audioIn: audioInputDevices[0]?.deviceId || '',
          audioOut: audioOutputDevices[0]?.deviceId || ''
        };

        // Validate stored devices exist in current device list
        const validVideoDevice = storedVideo && videoDevices.some(d => d.deviceId === storedVideo);
        const validAudioIn = storedAudioIn && audioInputDevices.some(d => d.deviceId === storedAudioIn);
        const validAudioOut = storedAudioOut && audioOutputDevices.some(d => d.deviceId === storedAudioOut);

        // Use stored device if valid, otherwise use default
        const selectedVideo = validVideoDevice ? storedVideo : defaults.video;
        const selectedAudioIn = validAudioIn ? storedAudioIn : defaults.audioIn;
        const selectedAudioOut = validAudioOut ? storedAudioOut : defaults.audioOut;

        console.log('Setting initial device IDs from cookies/defaults:', {
          selectedVideo,
          selectedAudioIn,
          selectedAudioOut
        });
        setVideoDeviceId(selectedVideo);
        setAudioInputDeviceId(selectedAudioIn);
        setAudioOutputDeviceId(selectedAudioOut);

        setCanJoin(true);
        setPermissionsError('');
        setPermissionsSuccess(true);
        setIsInitialized(true);

        // Hide success message after 3 seconds
        setTimeout(() => setPermissionsSuccess(false), 3000);
      } catch (error) {
        console.error("Permission Error:", error);
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setPermissionsError('Camera and Microphone permissions are required. Please grant access in your browser settings. After granting permissions, you can click the "Retry Permission Request" button below.');
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          setPermissionsError('No camera or microphone found. Please ensure they are connected and enabled.');
        } else if (error.name === 'NotReadableError') {
          setPermissionsError('Your camera or microphone is already in use by another application. Please close other applications using these devices and try again.');
        } else if (error.name === 'OverconstrainedError') {
          setPermissionsError('Unable to access your camera or microphone with the requested settings. Please try again.');
        } else {
          setPermissionsError('An error occurred while accessing media devices. Please check your browser settings and try again.');
        }
        setCanJoin(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkPermissionsAndInitialize();
  }, []);


  // Function to enumerate devices
  const enumerateDevices = async () => {
    console.log('enumerateDevices called, hasUserSelection:', hasUserSelection);
    try {
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        videoDevices: deviceList.filter(device => device.kind === 'videoinput'),
        audioInputDevices: deviceList.filter(device => device.kind === 'audioinput'),
        audioOutputDevices: deviceList.filter(device => device.kind === 'audiooutput')
      });

      // Handle case where selected device is no longer available
      const videoDevices = deviceList.filter(device => device.kind === 'videoinput');
      const audioInputDevices = deviceList.filter(device => device.kind === 'audioinput');
      const audioOutputDevices = deviceList.filter(device => device.kind === 'audiooutput');

      const videoExists = deviceList.some(d => d.deviceId === videoDeviceId);
      const audioInExists = deviceList.some(d => d.deviceId === audioInputDeviceId);
      const audioOutExists = deviceList.some(d => d.deviceId === audioOutputDeviceId);

      // Only reset devices if user hasn't made a selection and device no longer exists
      if (!hasUserSelection) {
        if (!videoExists && videoDevices.length > 0) {
          setVideoDeviceId(videoDevices[0].deviceId);
        } else if (!videoExists) {
          setVideoDeviceId('');
        }

        if (!audioInExists && audioInputDevices.length > 0) {
          setAudioInputDeviceId(audioInputDevices[0].deviceId);
        } else if (!audioInExists) {
          setAudioInputDeviceId('');
        }

        if (!audioOutExists && audioOutputDevices.length > 0) {
          setAudioOutputDeviceId(audioOutputDevices[0].deviceId);
        } else if (!audioOutExists) {
          setAudioOutputDeviceId('');
        }
      }
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  };

  // Monitor device changes and permission changes
  useEffect(() => {
    // Only enumerate devices if we're already initialized
    // Otherwise, let the initial effect handle it
    if (isInitialized) {
      enumerateDevices();
    }

    const handleDeviceChange = async () => {
      console.log('Devices changed');
      await enumerateDevices();

      // Check if we now have permissions (device labels are available)
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      const hasPermissions = deviceList.some(device => device.label !== '');

      if (hasPermissions && permissionsError) {
        // Permissions were just granted
        setPermissionsError('');
        setCanJoin(true);
        setPermissionsSuccess(true);
        setTimeout(() => setPermissionsSuccess(false), 3000);

        // Re-enumerate with proper labels
        const videoDevices = deviceList.filter(device => device.kind === 'videoinput');
        const audioInputDevices = deviceList.filter(device => device.kind === 'audioinput');
        const audioOutputDevices = deviceList.filter(device => device.kind === 'audiooutput');

        setDevices({
          videoDevices,
          audioInputDevices,
          audioOutputDevices
        });

        // Set default devices if none selected
        if (!videoDeviceId && videoDevices.length > 0) {
          setVideoDeviceId(videoDevices[0].deviceId);
        }
        if (!audioInputDeviceId && audioInputDevices.length > 0) {
          setAudioInputDeviceId(audioInputDevices[0].deviceId);
        }
        if (!audioOutputDeviceId && audioOutputDevices.length > 0) {
          setAudioOutputDeviceId(audioOutputDevices[0].deviceId);
        }
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [permissionsError]);

  // Replace video preview initialization
  useEffect(() => {
    const startVideoPreview = async () => {
      try {
        // Stop any existing stream
        if (activeStream) {
          activeStream.getTracks().forEach(track => track.stop());
        }

        if (!videoDeviceId || !audioInputDeviceId) return;

        const constraints = {
          video: videoDeviceId ? { deviceId: { ideal: videoDeviceId } } : true,
          audio: audioInputDeviceId ? { deviceId: { ideal: audioInputDeviceId } } : true
        };

        const newStream = await navigator.mediaDevices.getUserMedia(constraints);

        setActiveStream(newStream);
        setVideoStream(newStream);
        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
        }
      } catch (error) {
        console.error('Error starting video preview:', error);

        if (error.name === 'NotFoundError') {
          setPermissionsError('No camera or microphone found. Please check your device connections.');
        } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setPermissionsError('Camera and microphone access denied. Please allow access in your browser settings.');
        } else if (error.name === 'OverconstrainedError') {
          setPermissionsError('Unable to access the selected camera or microphone. Please try a different device.');
        } else {
          setPermissionsError('Failed to start video preview. Please check your camera and microphone.');
        }
      }
    };

    startVideoPreview();

    return () => {
      // Cleanup will be handled by handleJoin
    };
  }, [videoDeviceId, audioInputDeviceId]);

  // --- Save Settings Only ---
  const saveSettings = () => {
    console.log('saveSettings called');
    console.log('Current state before saving:', {
      videoDeviceId,
      audioInputDeviceId,
      audioOutputDeviceId,
      hasUserSelection,
      devices: {
        videoCount: devices.videoDevices.length,
        audioInputCount: devices.audioInputDevices.length,
        audioOutputCount: devices.audioOutputDevices.length
      }
    });

    // Log the actual device labels for debugging
    const selectedVideo = devices.videoDevices.find(d => d.deviceId === videoDeviceId);
    const selectedAudioIn = devices.audioInputDevices.find(d => d.deviceId === audioInputDeviceId);
    const selectedAudioOut = devices.audioOutputDevices.find(d => d.deviceId === audioOutputDeviceId);

    console.log('Selected devices:', {
      video: selectedVideo ? selectedVideo.label : 'Not found',
      audioIn: selectedAudioIn ? selectedAudioIn.label : 'Not found',
      audioOut: selectedAudioOut ? selectedAudioOut.label : 'Not found'
    });

    console.log('Saving settings:', {
      videoDeviceId,
      audioInputDeviceId,
      audioOutputDeviceId
    });

    // Create a fresh cookies instance to ensure we're not using stale data
    const cookiesInstance = new Cookies();

    // Clear existing cookies first to ensure clean slate
    cookiesInstance.remove('videoDeviceId', { path: '/' });
    cookiesInstance.remove('audioInputDeviceId', { path: '/' });
    cookiesInstance.remove('audioOutputDeviceId', { path: '/' });

    // Save settings to cookies with consistent names and options
    const cookieOptions = {
      path: '/',
      maxAge: 60 * 60 * 24 * 365, // 1 year
      sameSite: 'lax', // Changed from 'strict' to 'lax' for better compatibility
      secure: window.location.protocol === 'https:' // Only set secure flag on https
    };

    // Set new values
    cookiesInstance.set('videoDeviceId', videoDeviceId, cookieOptions);
    cookiesInstance.set('audioInputDeviceId', audioInputDeviceId, cookieOptions);
    cookiesInstance.set('audioOutputDeviceId', audioOutputDeviceId, cookieOptions);

    // Verify cookies were set using fresh instance
    const verifyInstance = new Cookies();
    console.log('Cookies after saving:', {
      videoDeviceId: verifyInstance.get('videoDeviceId'),
      audioInputDeviceId: verifyInstance.get('audioInputDeviceId'),
      audioOutputDeviceId: verifyInstance.get('audioOutputDeviceId')
    });

    // Show success message
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 3000);

    // If in test mode and onSetupComplete is provided, call it after saving
    if (isTestMode && onSetupComplete) {
      setTimeout(() => {
        onSetupComplete({
          videoDeviceId,
          audioInputDeviceId,
          audioOutputDeviceId
        });
      }, 1000); // Wait a bit to show the success message
    }
  };

  // --- Save Settings and Proceed ---
  const handleJoin = () => {
    // Save settings first
    saveSettings();

    // Stop any existing stream before joining
    if (activeStream) {
      activeStream.getTracks().forEach(track => track.stop());
    }

    // Go directly to meeting
    if (onSetupComplete) {
      onSetupComplete({
        videoDeviceId,
        audioInputDeviceId,
        audioOutputDeviceId
      });
    }
  };

  // Add cleanup on component unmount
  useEffect(() => {
    return () => {
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [activeStream]);

  // --- Speaker Test ---
  const playTestSound = () => {
    const audio = audioTestRef.current;
    if (!audio) return;

    const playAudio = () => {
      audio.currentTime = 0;
      audio.play().catch(e => {
        console.error("Error playing test sound:", e);
        alert("Could not play test sound.");
      });
    };

    if (audioOutputDeviceId && typeof audio.setSinkId === 'function') {
      audio.setSinkId(audioOutputDeviceId)
        .then(() => {
          console.log(`Audio output set to ${audioOutputDeviceId}`);
          playAudio();
        })
        .catch(e => {
          console.error(`Error setting audio output device ${audioOutputDeviceId}:`, e);
          // Attempt to play on default if setting sink fails
          playAudio();
        });
    } else {
      // Play on default device if sinkId not supported or no device selected
      playAudio();
    }
  };

  // Replace device selector section with this:
  const renderDeviceSelectors = () => (
    <div className="space-y-4">
      <CustomDeviceDropdown
        label="Camera"
        options={devices.videoDevices.map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Camera ${device.deviceId.slice(0, 5)}...`
        }))}
        value={videoDeviceId}
        onSelect={(id) => {
          console.log('Camera selected:', id, 'Previous:', videoDeviceId);
          const device = devices.videoDevices.find(d => d.deviceId === id);
          console.log('Selected camera device:', device ? device.label : 'Unknown');
          setVideoDeviceId(id);
          setHasUserSelection(true);
          setSettingsSaved(false);
        }}
        placeholder="Select camera"
      />

      <CustomDeviceDropdown
        label="Microphone"
        options={devices.audioInputDevices.map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 5)}...`
        }))}
        value={audioInputDeviceId}
        onSelect={(id) => {
          console.log('Microphone selected:', id, 'Previous:', audioInputDeviceId);
          setAudioInputDeviceId(id);
          setHasUserSelection(true);
          setSettingsSaved(false);
        }}
        placeholder="Select microphone"
      />

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Speakers
        </label>
        <div className="flex space-x-2">
          <div className="flex-1">
            <CustomDeviceDropdown
              label=""
              options={devices.audioOutputDevices.map(device => ({
                deviceId: device.deviceId,
                label: device.label || `Speaker ${device.deviceId.slice(0, 5)}...`
              }))}
              value={audioOutputDeviceId}
              onSelect={(id) => {
                console.log('Speaker selected:', id, 'Previous:', audioOutputDeviceId);
                setAudioOutputDeviceId(id);
                setHasUserSelection(true);
                setSettingsSaved(false);
              }}
              placeholder="Select speakers"
            />
          </div>
          <button
            onClick={playTestSound}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Test
          </button>
        </div>
      </div>
    </div>
  );

  // Replace video preview render method
  const renderVideoPreview = () => (
    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative">
      <video
        ref={videoRef}
        className="w-full h-full transform scale-x-[-1]"
        autoPlay
        playsInline
        muted
      />
    </div>
  );

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Device Setup</h2>

          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Checking permissions...</span>
            </div>
          )}

          {permissionsSuccess && !isLoading && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    Permissions granted successfully! Your devices are now available.
                  </p>
                </div>
              </div>
            </div>
          )}

          {settingsSaved && !isLoading && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Settings saved successfully! Your device preferences will be remembered.
                  </p>
                </div>
              </div>
            </div>
          )}

          {permissionsError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-red-700">{permissionsError}</p>
                  {permissionsError.includes('permissions are required') && (
                    <div className="mt-4">
                      <button
                        onClick={() => window.location.reload()}
                        className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <svg className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" />
                        </svg>
                        Retry Permission Request
                      </button>
                      <p className="text-xs text-gray-600 mt-2">
                        After granting permissions in your browser, click this button to reload the page.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!isLoading && !permissionsError && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Video Preview */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Camera Preview</h3>
                  {renderVideoPreview()}
                </div>

                {/* Device Controls */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Audio & Video Settings</h3>
                  {renderDeviceSelectors()}
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {isTestMode ? (
                  <button
                    onClick={saveSettings}
                    disabled={!canJoin}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    Save Settings
                  </button>
                ) : (
                  <>
                    <button
                      onClick={saveSettings}
                      disabled={!canJoin}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      Save Settings
                    </button>
                    <button
                      onClick={handleJoin}
                      disabled={!canJoin}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      Save & Join Meeting
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          <audio
            ref={audioTestRef}
            src={testSoundUrl}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
  );
}

export default DeviceSetup;